import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  AutoGraph as AutoGraphIcon,
  Campaign as CampaignIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Savings as SavingsIcon,
  Speed as SpeedIcon,
  VerifiedUser as VerifiedUserIcon,
  YouTube as YouTubeIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { connectionRequestsApi, publicLandingApi } from '../services/api';
import type { ExistingOrdType, LandingPromo } from '../types';

const START_UNIT = 40;
const START_PACK = 1000;
const BUSINESS_MONTH = 3000;

const startPros: { text: string; star?: boolean }[] = [
  { text: 'Максимальная автоматизация процессов — акты, статистика и др.' },
  { text: 'Намного дешевле, чем оплата ОРД напрямую' },
  { text: 'Можно покупать креативы поштучно' },
  { text: 'Не нужен свой кабинет ОРД — работаете через Тренди' },
  { text: 'YouTube, VK, Rutube и Telegram в одном контуре' },
  { text: 'Легко полностью отказаться или перейти на другой тариф' },
  { text: 'Акты и статистика сдаются всегда без пропусков, ошибок и опозданий' },
];

const businessPros: { text: string; star?: boolean }[] = [
  { text: 'Подключение к уже имеющемуся ОРД' },
  { text: 'Выбор оператора: VK, Яндекс или Ozon' },
  { text: 'Все договоры, акты, креативы, статистика сдаются в ваш ОРД' },
  { text: 'Импорт уже существующих креативов из ОРД', star: true },
  { text: 'Максимальная автоматизация процессов' },
  { text: 'YouTube, VK, Rutube и Telegram в одном контуре' },
  { text: 'Выдача доступа сотрудникам и постоянным контрагентам с чёткими правилами — без возможности что-то случайно нарушить' },
  { text: 'Лимиты рекламы на контрагентов и мониторинг их каналов' },
  { text: 'Вариант без автосдачи статистики за 1 500 ₽' },
  { text: 'Акты и статистика сдаются всегда без пропусков, ошибок и опозданий' },
  { text: 'Легко полностью отказаться или перейти на другой тариф, не потеряв креативы и статистику' },
];

function TariffPros({ items }: { items: { text: string; star?: boolean }[] | string[] }) {
  const normalized = items.map((item) =>
    typeof item === 'string' ? { text: item } : item
  );
  return (
    <Stack spacing={1} component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {normalized.map((item) => (
        <Stack
          key={item.text}
          component="li"
          direction="row"
          spacing={1}
          alignItems="flex-start"
        >
          <CheckIcon color="primary" sx={{ fontSize: 18, mt: '2px', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            {item.text}
            {item.star ? ' *' : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

const benefits = [
  {
    icon: <AutoGraphIcon />,
    title: 'Полная автоматизация',
    text: 'Сбор просмотров с VK, YouTube, Rutube и Telegram, формирование актов и сдача статистики в ОРД — без ручной рутины каждый месяц.',
  },
  {
    icon: <VerifiedUserIcon />,
    title: 'Подключение уже существующего ОРД',
    text: 'Если у вас уже есть кабинет VK, Яндекс или Ozon ОРД — подключаем его к автоматизации: существующие креативы* и новые через наш интерфейс.',
  },
  {
    icon: <SpeedIcon />,
    title: 'Простое оформление рекламы',
    text: 'Контрагенты, договоры и креативы создаются в понятных формах — без сложных экранов и лишних шагов.',
  },
  {
    icon: <CampaignIcon />,
    title: 'Креативы и ERID под контролем',
    text: 'Регистрация креативов, привязка ссылок на ролики, отслеживание статусов и ошибок — всё в одном месте.',
  },
  {
    icon: <SavingsIcon />,
    title: 'Гибкие тарифы от 40 ₽',
    text: '«Старт» — поштучно или пакетами. «Бизнес» — автоматизация вашего ОРД без обязательной смены оператора.',
  },
  {
    icon: <BusinessIcon />,
    title: 'Свой кабинет под ваш бизнес',
    text: 'Отдельный доступ для вас, ваших менеджеров и монтажёров — свои лимиты и настройки.',
  },
];

const steps = [
  { num: '01', title: 'Оставляете заявку', text: 'Расскажите, сколько креативов в месяц и какой ОРД уже используете.' },
  { num: '02', title: 'Подключаем кабинет', text: 'Настраиваем доступ, тариф и при необходимости помогаем с миграцией данных.' },
  { num: '03', title: 'Работаете как обычно', text: 'Создаёте контрагентов, договоры и креативы — система сдаёт статистику сама.' },
];

const ordOptions: { value: ExistingOrdType; label: string }[] = [
  { value: 'NONE', label: 'Пока нет ОРД' },
  { value: 'VK', label: 'VK ОРД' },
  { value: 'YANDEX', label: 'Яндекс ОРД' },
  { value: 'OZON', label: 'Ozon ОРД' },
  { value: 'OTHER', label: 'Другой / несколько' },
];

function discounted(price: number, percent: number): number {
  return Math.round(price * (100 - percent) / 100);
}

function promoHeadline(promo: LandingPromo | null): string | null {
  if (!promo?.enabled) return null;
  const parts: string[] = [];
  if (promo.businessFirstMonthFree) {
    parts.push('«Бизнес» 1-й месяц бесплатно');
  } else if (promo.discountBusiness) {
    parts.push(`−${promo.discountPercent}% на «Бизнес»`);
  }
  if (promo.discountStart) {
    parts.push(`−${promo.discountPercent}% на «Старт»`);
  }
  if (parts.length === 0) return null;
  return `Акция: ${parts.join(' · ')}`;
}

const LandingPage: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [messenger, setMessenger] = useState('');
  const [creativesPerMonth, setCreativesPerMonth] = useState<number | ''>('');
  const [existingOrd, setExistingOrd] = useState<ExistingOrdType>('NONE');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [promo, setPromo] = useState<LandingPromo | null>(null);

  useEffect(() => {
    publicLandingApi.getPromo()
      .then(setPromo)
      .catch(() => setPromo(null));
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const promoOn = !!promo?.enabled
    && (!!promo.businessFirstMonthFree || !!promo.discountStart || !!promo.discountBusiness);
  const pct = promo?.discountPercent ?? 50;
  const startDiscounted = promoOn && !!promo!.discountStart;
  const businessFree = promoOn && !!promo!.businessFirstMonthFree;
  const businessDiscounted = promoOn && !businessFree && !!promo!.discountBusiness;
  const startUnitPrice = startDiscounted ? discounted(START_UNIT, pct) : START_UNIT;
  const startPackPrice = startDiscounted ? discounted(START_PACK, pct) : START_PACK;
  const businessPrice = businessFree ? 0 : businessDiscounted ? discounted(BUSINESS_MONTH, pct) : BUSINESS_MONTH;
  const headline = promoHeadline(promo);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!email.trim() && !messenger.trim()) {
      setError('Укажите email или контакт в Telegram / VK / MAX');
      return;
    }
    const count = Number(creativesPerMonth);
    if (!count || count < 1) {
      setError('Укажите желаемое количество креативов в месяц');
      return;
    }
    try {
      setSaving(true);
      await connectionRequestsApi.create({
        contactName: contactName.trim() || undefined,
        email: email.trim() || undefined,
        messenger: messenger.trim() || undefined,
        creativesPerMonth: count,
        existingOrd,
        comment: comment.trim() || undefined,
      });
      setSuccess(true);
      setContactName('');
      setEmail('');
      setMessenger('');
      setCreativesPerMonth('');
      setExistingOrd('NONE');
      setComment('');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось отправить заявку');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100%', maxWidth: '100%', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1.25 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '11px',
                  background: 'linear-gradient(135deg, #0F766E 0%, #0891B2 100%)',
                  boxShadow: '0 4px 14px rgba(15, 118, 110, 0.3)',
                }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                  TrendyORD
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ОРД без рутины
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button color="inherit" onClick={scrollToForm} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Заявка
              </Button>
              <Button component={RouterLink} to="/login" variant="outlined" size="small">
                Вход в ЛК
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 5, md: 8 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 10% -20%, rgba(20, 184, 166, 0.22), transparent 55%),' +
              'radial-gradient(ellipse 60% 50% at 90% 10%, rgba(3, 105, 161, 0.14), transparent 50%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {headline && (
                  <Chip
                    label={headline}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                )}
                <Chip
                  label="3 ОРД · YouTube · VK Видео · Rutube · Telegram"
                  size="small"
                  sx={{
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    color: 'primary.dark',
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.04em',
                  lineHeight: 1.1,
                  fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' },
                  mb: 2,
                }}
              >
                Маркировка рекламы и отчётность в ОРД —{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>
                  на автопилоте
                </Box>
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 3, maxWidth: 560 }}>
                Сдаём в VK ОРД, Яндекс ОРД и Ozon ОРД. Собираем просмотры с YouTube, VK Видео, Rutube и Telegram.
                Создавайте контрагентов, договоры и креативы без лишней возни — от 40 ₽ за креатив.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" size="large" onClick={scrollToForm} endIcon={<SendIcon />}>
                  Оставить заявку
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href="https://vk.ru/kazakovie"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Написать в VK
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: (t) =>
                    `linear-gradient(145deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha('#fff', 0.9)} 60%)`,
                }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {promoOn ? 'Спецпредложение на первый месяц' : 'Экономия на доступе к ОРД'}
                </Typography>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ТрендиОРД «Старт»
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      {startDiscounted && (
                        <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '1.1rem', mr: 1, fontWeight: 600 }}>
                          {START_UNIT} ₽
                        </Box>
                      )}
                      от {startUnitPrice} ₽
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}/ креатив
                      </Typography>
                    </Typography>
                    {startDiscounted && (
                      <Typography variant="caption" color="secondary.main" fontWeight={700}>
                        −{pct}% на первый месяц
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ТрендиОРД «Бизнес»
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: businessFree || businessDiscounted ? 'primary.main' : 'primary.main' }}>
                      {(businessFree || businessDiscounted) && (
                        <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '1rem', mr: 1, fontWeight: 600 }}>
                          {BUSINESS_MONTH.toLocaleString('ru-RU')} ₽
                        </Box>
                      )}
                      {businessFree
                        ? '0 ₽'
                        : businessDiscounted
                          ? `${businessPrice.toLocaleString('ru-RU')} ₽`
                          : 'от 1 500 ₽'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}/ мес
                      </Typography>
                    </Typography>
                    {businessFree && (
                      <Typography variant="caption" color="secondary.main" fontWeight={700}>
                        Первый месяц бесплатно
                      </Typography>
                    )}
                    {businessDiscounted && !businessFree && (
                      <Typography variant="caption" color="secondary.main" fontWeight={700}>
                        −{pct}% на первый месяц
                      </Typography>
                    )}
                  </Box>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
                        ОРД
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label="VK ОРД" size="small" color="primary" />
                        <Chip label="Яндекс ОРД" size="small" color="primary" />
                        <Chip label="Ozon ОРД" size="small" color="primary" />
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
                        Площадки
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip icon={<YouTubeIcon />} label="YouTube" size="small" variant="outlined" />
                        <Chip label="VK Видео" size="small" variant="outlined" />
                        <Chip label="Rutube" size="small" variant="outlined" />
                        <Chip icon={<TelegramIcon />} label="Telegram" size="small" variant="outlined" />
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 1 }}>
          Почему TrendyORD
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Мы закрываем всю цепочку: от креатива и ERID до ежемесячной отчётности по просмотрам.
        </Typography>
        <Grid container spacing={2}>
          {benefits.map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(15, 118, 110, 0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    mb: 1.5,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Tariffs */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
            Тарифы
          </Typography>
          {headline && <Chip label={headline} color="secondary" size="small" sx={{ fontWeight: 700 }} />}
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          {promoOn
            ? 'Цены ниже — по акции на первый месяц. Дальше — обычный тариф. Неиспользованные креативы на «Старте» сгорают в конце биллингового периода.'
            : 'Выберите формат под ваш объём рекламы. Неиспользованные креативы на «Старте» сгорают в конце биллингового периода.'}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2.5, height: '100%', borderColor: (t) => alpha(t.palette.primary.main, 0.35) }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip label="Старт" color="primary" size="small" sx={{ fontWeight: 700 }} />
                {startDiscounted && (
                  <Chip label={`−${pct}% первый месяц`} color="secondary" size="small" variant="outlined" />
                )}
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {startDiscounted ? (
                  <>
                    <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '1.1rem', mr: 1 }}>
                      от {START_UNIT} ₽
                    </Box>
                    От {startUnitPrice} ₽ за креатив
                  </>
                ) : (
                  <>От {START_UNIT} ₽ за креатив</>
                )}
              </Typography>
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{startUnitPrice} ₽</strong>
                  {startDiscounted && (
                    <Box component="span" sx={{ textDecoration: 'line-through', ml: 0.75, opacity: 0.7 }}>
                      {START_UNIT} ₽
                    </Box>
                  )}
                  {' '}— поштучно за 1 креатив
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{startPackPrice.toLocaleString('ru-RU')} ₽</strong>
                  {startDiscounted && (
                    <Box component="span" sx={{ textDecoration: 'line-through', ml: 0.75, opacity: 0.7 }}>
                      {START_PACK.toLocaleString('ru-RU')} ₽
                    </Box>
                  )}
                  {' '}— пакет из 31 креатива
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Подходит физлицам, самозанятым и ИП, у кого до 31 рекламы в месяц.
              </Typography>
              <TariffPros items={startPros} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip label="Бизнес" color="secondary" size="small" sx={{ fontWeight: 700 }} />
                {businessFree && (
                  <Chip label="1-й месяц бесплатно" color="secondary" size="small" variant="outlined" />
                )}
                {businessDiscounted && !businessFree && (
                  <Chip label={`−${pct}% первый месяц`} color="secondary" size="small" variant="outlined" />
                )}
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {(businessFree || businessDiscounted) ? (
                  <>
                    <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '1.1rem', mr: 1 }}>
                      {BUSINESS_MONTH.toLocaleString('ru-RU')} ₽
                    </Box>
                    {businessFree ? '0 ₽' : `${businessPrice.toLocaleString('ru-RU')} ₽`} / мес
                  </>
                ) : (
                  <>от 1 500 ₽ / мес</>
                )}
              </Typography>
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>3 000 ₽</strong> — полный «Бизнес» со сдачей статистики (VK / Яндекс / Ozon)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>1 500 ₽</strong> — без сдачи статистики (Яндекс / Ozon): креативы, договоры и акты у нас;
                  статистику клиент сдаёт в ЛК ОРД (у Ozon — встроенные шаблоны автосдачи)
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Подходит крупным рекламодателям и агентствам.
              </Typography>
              <TariffPros items={businessPros} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                Услуги самого ОРД оплачиваются отдельно по тарифам оператора.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.04), py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 3 }}>
            Как это работает
          </Typography>
          <Grid container spacing={2}>
            {steps.map((step) => (
              <Grid size={{ xs: 12, md: 4 }} key={step.num}>
                <Paper sx={{ p: 2.5, height: '100%' }}>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, color: (t) => alpha(t.palette.primary.main, 0.25), lineHeight: 1, mb: 1 }}
                  >
                    {step.num}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Form + contacts */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }} ref={formRef}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 1.5 }}>
              Подключение и вопросы
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2.5 }}>
              Оставьте заявку — мы свяжемся и подберём тариф. Или напишите напрямую:
            </Typography>
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TelegramIcon color="primary" fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Telegram
                    </Typography>
                    <Typography variant="body2" fontWeight={600} display="block">
                      @tp_trendy_eng_shop
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <DescriptionIcon color="primary" fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      VK
                    </Typography>
                    <Link
                      href="https://vk.ru/kazakovie"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      variant="body2"
                      display="block"
                      fontWeight={600}
                    >
                      vk.ru/kazakovie
                    </Link>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon color="primary" fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Почта
                    </Typography>
                    <Link
                      href="mailto:igor.kazakov.evg@mail.ru"
                      underline="hover"
                      variant="body2"
                      display="block"
                      fontWeight={600}
                      sx={{ wordBreak: 'break-all' }}
                    >
                      igor.kazakov.evg@mail.ru
                    </Link>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }} component="form" onSubmit={submit}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Заявка на подключение
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Укажите контакты и примерный объём рекламы в месяц — этого достаточно для первого шага.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Имя или компания"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      helperText="Email или мессенджер — хотя бы одно"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Telegram / VK / MAX"
                      value={messenger}
                      onChange={(e) => setMessenger(e.target.value)}
                      fullWidth
                      placeholder="@username или ссылка"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Креативов в месяц, шт."
                      type="number"
                      required
                      value={creativesPerMonth}
                      onChange={(e) => setCreativesPerMonth(e.target.value === '' ? '' : Number(e.target.value))}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label="Уже есть ОРД?"
                      value={existingOrd}
                      onChange={(e) => setExistingOrd(e.target.value as ExistingOrdType)}
                      fullWidth
                    >
                      {ordOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <TextField
                  label="Дополнительно"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Тариф, каналы, особенности учёта, сроки запуска…"
                />
                {error && <Alert severity="error">{error}</Alert>}
                {success && (
                  <Alert severity="success">
                    Заявка отправлена! Мы свяжемся с вами в ближайшее время.
                  </Alert>
                )}
                <Button type="submit" variant="contained" size="large" disabled={saving} endIcon={<SendIcon />}>
                  {saving ? <CircularProgress size={22} color="inherit" /> : 'Отправить заявку'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          py: 3,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} TrendyORD · маркировка и отчётность в ОРД
              </Typography>
              <Button component={RouterLink} to="/login" size="small" color="inherit">
                Вход в личный кабинет
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 920, lineHeight: 1.5 }}>
              * Автоматической выгрузки старых данных из Яндекс ОРД нет — доступна только ручная выгрузка
              информации (Excel в кабинете Тренди). Для новых креативов, актов и площадок, созданных через
              ТрендиОРД, доступен полный функционал. У VK ОРД и Ozon ОРД импорт списков из API поддерживается.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
